import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const navLinks = [
  { name: 'Forside', href: '/' },
  { name: 'Om Os', href: '/om-os' },
  { name: 'Rengøring', href: '/ydelser/rengoering' },
  { name: 'Polering', href: '/ydelser/polering' },
  { name: 'Coating', href: '/ydelser/coating' },
  { name: 'Kontakt', href: '/kontakt' },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <nav
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b',
        (scrolled || isOpen)
          ? 'bg-black py-3 border-white/10'
          : 'bg-transparent py-6 border-transparent'
      )}
    >
      <div className="container mx-auto px-4 flex items-center justify-between">
        <Link to="/" className="flex items-center">
          <img 
            src="https://assets.auxodev.dk/assets/f1317603-54a7-41be-bffe-0256114ce69f/47edd071-9b8e-4f1e-9174-ebd78556e2c5.jpg" 
            alt="MJ Detailing Logo" 
            className="h-20 w-auto object-contain"
          />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center space-x-8">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.href}
              className={cn(
                'text-sm font-medium uppercase tracking-widest transition-colors hover:text-white',
                location.pathname === link.href ? 'text-white' : 'text-white/60'
              )}
            >
              {link.name}
            </Link>
          ))}
          <a
            href="tel:+4527895995"
            className="flex items-center gap-2 bg-white text-black px-5 py-2.5 rounded-full text-sm font-bold hover:bg-zinc-200 transition-colors"
          >
            <Phone size={16} />
            27 89 59 95
          </a>
        </div>

        {/* Mobile Toggle */}
        <button
          className="lg:hidden flex items-center gap-3 text-white group focus:outline-none"
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? 'Luk menu' : 'Åbn menu'}
        >
          <span className={cn(
            "text-[10px] font-bold uppercase tracking-[0.2em] transition-opacity",
            isOpen ? "opacity-100 text-white" : "opacity-60 group-hover:opacity-100"
          )}>
            {isOpen ? 'Luk' : 'Menu'}
          </span>
          <div className="relative w-6 h-6">
            <div className={cn(
              "absolute inset-0 transition-all duration-300 flex items-center justify-center",
              isOpen ? "opacity-0 rotate-90 scale-50" : "opacity-100 rotate-0 scale-100"
            )}>
              <Menu size={24} />
            </div>
            <div className={cn(
              "absolute inset-0 transition-all duration-300 flex items-center justify-center",
              isOpen ? "opacity-100 rotate-0 scale-100" : "opacity-0 -rotate-90 scale-50"
            )}>
              <X size={24} />
            </div>
          </div>
        </button>
      </div>

      {/* Mobile Nav */}
      <div
        className={cn(
          'fixed inset-0 bg-black z-40 lg:hidden transition-transform duration-500 ease-in-out pt-24 px-6',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        <div className="flex flex-col space-y-6">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.href}
              className={cn(
                'text-3xl font-bold uppercase tracking-normal',
                location.pathname === link.href ? 'text-white' : 'text-white/40'
              )}
            >
              {link.name}
            </Link>
          ))}
          <a
            href="tel:+4527895995"
            className="flex items-center justify-center gap-3 bg-white text-black py-4 rounded-xl text-lg font-black mt-4"
          >
            <Phone size={20} />
            Ring Nu
          </a>
        </div>
      </div>
    </nav>
  );
}
